import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import algoliasearch from 'algoliasearch/lite';
import {
  Configure,
  Index,
  connectStateResults,
  connectSearchBox,
  InstantSearch,
  connectHitInsights,
  connectHits,
} from 'react-instantsearch-dom';
import OutsideClickHandler from 'react-outside-click-handler';
import { aa } from '@repo/utils/algoliaAnalytics';
import { LocaleContext } from '@repo/contexts/LocaleContext';
import { useLocalizedNavigation } from '@repo/gatsby-localization-helpers';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import { SiteSettingsContext } from '@repo/contexts/SiteSettingsContext';

import styles from '../styles';
import LinkComponent from '../LinkComponent';
import SearchForm from './search-form/SearchForm';
import SearchFormHit from './search-form/SearchFormHit';
import breakpoints from '../breakpoints';

const CustomSearchFormHit = connectHitInsights(aa)(SearchFormHit);

const HitsContainer = styled.ul`
  list-style: none;
  position: absolute;
  left: 0;
  right: 0;
  top: 54px;
  background: white;
  padding-left: 0;
  color: ${({ theme }) => theme.colors.navSearchHitsContainerText};
  h2 {
    padding: ${styles.spacing['spacing-sm']} ${styles.spacing['spacing-md']} 0;
    padding-inline: 0.5rem;
    margin: 0;
  }
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  li {
    &:last-child > div {
      border-bottom: none;
    }
  }
  box-shadow: 0 2.8px 2.2px rgba(0, 0, 0, 0.02),
    0 6.7px 5.3px rgba(0, 0, 0, 0.028), 0 12.5px 10px rgba(0, 0, 0, 0.035),
    0 22.3px 17.9px rgba(0, 0, 0, 0.042), 0 41.8px 33.4px rgba(0, 0, 0, 0.05),
    0 100px 80px rgba(0, 0, 0, 0.07);

  @media (min-width: ${breakpoints.lg}) {
    width: 328px;
  }

  @media (min-width: ${breakpoints.xl}) {
    width: 25rem;
  }

  .ais-Hits-item {
    padding: 0;
    box-shadow: none;
  }
`;

const UnavailableMessage = styled.div`
  background: inherit;
  position: absolute;
  width: 100%;
  color: white;
  padding: 0.75rem 1rem;
  font-weight: normal;
  font-size: 0.875rem;
`;
const getNumberOfHitsVisible = allSearchResults =>
  Object.values(allSearchResults ?? {}).reduce(
    (prev, curr) => prev + curr.hits.length,
    0
  );

const ConnectedHitsArea = connectStateResults(
  ({ allSearchResults, searchState, children, onKeyDown, resultsRef, tt }) => {
    // add up the total number of visible search results.
    const numberOfHits = getNumberOfHitsVisible(allSearchResults);
    const query = searchState?.query;
    // We need to not should <ul> if there are not hits
    if (!query || !numberOfHits) return children;
    return (
      <HitsContainer
        id="hits-container"
        aria-label={tt(s => s.navSearchComponent.hitsContainerAriaLabel)}
        role="listbox"
        onKeyDown={onKeyDown} // For arrow key navigation
        ref={resultsRef}
      >
        {children}
      </HitsContainer>
    );
  }
);

const AllResultsLink = styled(LinkComponent)`
  background: revert;
  display: block;
  color: ${({ theme }) => theme.colors.navSearchAllResultsColor};
  padding: ${styles.spacing['spacing-sm']} ${styles.spacing['spacing-md']};
  font-weight: normal;
  font-size: ${styles.fontSize.base};
`;

const CustomSearchBox = connectSearchBox(SearchForm);

const ConnectedHits = connectHits(({ hits }) => (
  <>
    {hits.map(hit => (
      <li key={hit.objectID}>
        <CustomSearchFormHit hit={hit} />
      </li>
    ))}
  </>
));

/**
 * This invisible component will read the number of search results if they change to the screen reader
 */
const ScreenReaderAnnouncement = connectStateResults(
  ({ allSearchResults, query, tt }) => {
    // add up the total number of visible search results
    const numberOfHits = getNumberOfHitsVisible(allSearchResults);
    return (
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {query && numberOfHits > 0
          ? tt(s => s.navSearchComponent.screenReaderAnnouncementMessage, {
              numberOfHits,
            })
          : tt(
              s => s.navSearchComponent.screenReaderAnnouncementNoResultsMessage
            )}
      </div>
    );
  }
);

let firstLoad = true;

const algoliaClient = algoliasearch(
  process.env.GATSBY_ALGOLIA_APP_ID,
  process.env.GATSBY_ALGOLIA_SEARCH_KEY
);

function NavSearch({ searchIndexes, hitsPerPage = 5 }) {
  const localeContext = useContext(LocaleContext);
  const [activeSearchState, setActiveSearchState] = useState({});
  const [apiError, setApiError] = useState(false);
  const currency = localeContext?.currency || 'USD';

  const { tt } = useUiComponentsInternationalContext();
  const { algoliaSearchSettings } = useContext(SiteSettingsContext);

  const {
    blogIndexName,
    getAlgoliaBlogsFilter,
    productIndexName,
    getAlgoliaProductsFilter,
  } = algoliaSearchSettings;

  const navigate = useLocalizedNavigation();

  let searchRes;

  // If there is a search query the results list should be "expanded", even if no results
  const resultsListExpanded = !!activeSearchState?.query;

  const Results = connectStateResults(
    ({ searchState, searchResults, children }) => {
      if (searchState && searchResults?.hits?.length && searchState.query) {
        searchRes = searchResults;
        const tabForIndex = searchIndexes.indexOf(searchResults?.index || 0);
        return (
          <>
            {children}
            <AllResultsLink
              to={
                currency === 'CAD'
                  ? `/en-ca/search?query=${searchState.query}&tabIndex=${tabForIndex}`
                  : `/search?query=${searchState.query}&tabIndex=${tabForIndex}`
              }
              state={{ searchResults }}
              tabIndex={-1} // Don't focus on TAB since we use arrow keys to navigate inside the result box
            >
              {tt(s => s.headerCommon.showAllResultsForQueryLink, {
                query: searchState.query,
              })}
            </AllResultsLink>
          </>
        );
      }
      return null;
    }
  );

  const searchClient = {
    search(requests) {
      // prevent request on first load
      if (firstLoad === true) {
        firstLoad = false;
        return;
      }

      // prevent request if no params
      if (requests.every(({ params }) => !params.query)) {
        return;
      }
      // eslint-disable-next-line consistent-return
      return algoliaClient.search(requests).catch(() => setApiError(true));
    },
  };

  // all refs used for ADA arrow navigation
  const searchWrapperRef = useRef(null);
  const searchResultsContainerRef = useRef(null);
  const focusedCount = useRef(0);

  // Each key stroke corresponds to a unique integer
  const KEYS_MAP = {
    TAB: 9,
    UP: 38,
    DOWN: 40,
    ESC: 27,
  };

  /**
   * Handles the event when the user types inside the search box.
   * This is necessary for ADA compliance to allow the user navigate with arrow keys on search results
   */
  const handleKeyDown = e => {
    // A list of all interactable components in the search that the user can focus on
    const focusableElementsInNavSearch = [
      searchWrapperRef.current?.querySelector('input'), // The search bar input
      ...Array.from(searchWrapperRef.current?.querySelectorAll('a') ?? []), // All links in autocomplete menu
    ];
    // Determine which key was pressed and move focus accordingly
    switch (e.which) {
      case KEYS_MAP.DOWN:
        e.preventDefault();
        e.stopPropagation();
        // increment the count. use modulo to hav cycle functionality
        focusedCount.current =
          (focusedCount.current + 1) % focusableElementsInNavSearch.length;
        focusableElementsInNavSearch?.[focusedCount.current]?.focus();
        break;
      case KEYS_MAP.UP:
        e.preventDefault();
        e.stopPropagation();
        // decrement the count. use modulo to hav cycle functionality
        focusedCount.current =
          (focusedCount.current - 1 + focusableElementsInNavSearch.length) %
          focusableElementsInNavSearch.length;
        focusableElementsInNavSearch?.[focusedCount.current]?.focus();
        break;
      case KEYS_MAP.TAB:
        // Clears results and focus on next element, rather than focusing on items in the search results
        setActiveSearchState({});
        break;
      case KEYS_MAP.ESC:
        e.preventDefault();
        e.stopPropagation();
        e.target.blur();
        setActiveSearchState({});
        break;
      default:
        // Any other key press should behave normally
        break;
    }
  };

  // Reset the focused count when no results are displayed to prevent jumping to last item in list that was focused on
  useEffect(() => {
    if (!activeSearchState.query) {
      focusedCount.current = 0;
    }
  }, [activeSearchState]);

  return (
    <div ref={searchWrapperRef}>
      <OutsideClickHandler
        onOutsideClick={() => {
          // Resets the search results and query to blank/nothing
          setActiveSearchState({});
        }}
      >
        <InstantSearch
          searchClient={searchClient}
          indexName={productIndexName}
          onSearchStateChange={newSearchState =>
            setActiveSearchState(newSearchState)
          }
          searchState={activeSearchState}
        >
          {/* Used for screen reader announcing search results suggestions */}
          <ScreenReaderAnnouncement
            query={activeSearchState?.query ?? ''}
            tt={tt}
          />
          <CustomSearchBox
            brandName="Brand Name"
            onKeyDown={handleKeyDown} // passed down for arrow key navigation
            searchResultsContainerRef={searchResultsContainerRef}
            ariaExpanded={resultsListExpanded}
            onSubmit={evt => {
              evt.preventDefault();

              let searchUrl = currency === 'CAD' ? `/en-ca/search` : `/search`;

              if (activeSearchState?.query) {
                searchUrl = `${searchUrl}?query=${activeSearchState.query}`;
              }

              navigate(searchUrl, {
                state: {
                  searchResults: searchRes,
                },
              });
            }}
          />
          {apiError ? (
            <OutsideClickHandler
              onOutsideClick={() => {
                setApiError(false);
              }}
            >
              <UnavailableMessage>
                {tt(s => s.headerCommon.searchIsCurrentlyUnavailableMessage)}
              </UnavailableMessage>
            </OutsideClickHandler>
          ) : (
            <ConnectedHitsArea
              onKeyDown={handleKeyDown} // passed down for arrow key navigation
              resultsRef={searchResultsContainerRef}
              tt={tt}
            >
              {searchIndexes?.includes('pages') && (
                <Index indexName="pages">
                  <Configure
                    hitsPerPage={hitsPerPage}
                    clickAnalytics
                    filters={
                      currency === 'CAD'
                        ? 'language:en-ca OR language:en-us'
                        : 'language:en OR language:en-us'
                    }
                  />
                  <Results indexName="pages">
                    <ConnectedHits />
                  </Results>
                </Index>
              )}
              {searchIndexes?.includes(productIndexName) && (
                <Index indexName={productIndexName}>
                  <Configure
                    hitsPerPage={hitsPerPage}
                    clickAnalytics
                    filters={
                      currency === 'CAD'
                        ? 'NOT canBeShown:false AND tags:sold-in-canada'
                        : getAlgoliaProductsFilter()
                    }
                  />
                  <Results indexName={productIndexName}>
                    <h2>{tt(s => s.headerCommon.productsHeader)}</h2>
                    <ConnectedHits />
                  </Results>
                </Index>
              )}
              {searchIndexes?.includes(blogIndexName) && (
                <Index indexName={blogIndexName}>
                  <Configure
                    hitsPerPage={hitsPerPage}
                    clickAnalytics
                    filters={getAlgoliaBlogsFilter()}
                  />
                  <Results indexName={blogIndexName}>
                    <h2>{process.env.GATSBY_BLOG_NAME}</h2>
                    <ConnectedHits />
                  </Results>
                </Index>
              )}
            </ConnectedHitsArea>
          )}
        </InstantSearch>
      </OutsideClickHandler>
    </div>
  );
}

NavSearch.propTypes = {
  searchIndexes: PropTypes.arrayOf(PropTypes.string).isRequired,
  hitsPerPage: PropTypes.number,
};

export default NavSearch;
